import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import styled from 'styled-components'
import { Form, Input, Button, DatePicker, message, Card, Space } from 'antd'
import { CheckCircleOutlined, ArrowLeftOutlined } from '@ant-design/icons'
import api from '../utils/api'
import { now as tzNow } from '../utils/timezone'

const { TextArea } = Input

const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #f5f5f0 0%, #e8e8e8 50%, #f0ede5 100%);
  padding: 20px;

  @media (max-width: 768px) {
    padding: 15px 10px;
  }
`

const ContentWrapper = styled.div`
  max-width: 800px;
  margin: 0 auto;
`

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px;
`

const Logo = styled.img`
  width: 80px;
  height: 80px;
  margin-bottom: 20px;

  @media (max-width: 768px) {
    width: 60px;
    height: 60px;
  }
`

const Title = styled.h1`
  font-size: 28px;
  color: #5a5a5a;
  margin: 0 0 10px 0;
  font-weight: 500;
  letter-spacing: 0.5px;

  @media (max-width: 768px) {
    font-size: 24px;
  }
`

const Subtitle = styled.p`
  color: #8a8a8a;
  font-size: 16px;
  margin: 0;
  font-weight: 300;

  @media (max-width: 768px) {
    font-size: 14px;
  }
`

const FormCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: #faf9f6;
  border: 1px solid rgba(168, 181, 192, 0.2);

  .ant-card-body {
    padding: 30px;
  }

  @media (max-width: 768px) {
    .ant-card-body {
      padding: 20px;
    }
  }
`

const FormItem = styled(Form.Item)`
  .ant-form-item-label > label {
    color: #5a5a5a;
    font-weight: 500;
  }

  .ant-input,
  .ant-picker {
    border-radius: 8px;
    border-color: #d4c4b0;

    &:focus,
    &.ant-picker-focused {
      border-color: #a8b5c0;
      box-shadow: 0 0 0 3px rgba(168, 181, 192, 0.15);
    }
  }

  .ant-input::placeholder {
    color: #b8b8b8;
  }
`

const SubmitButton = styled(Button)`
  width: 100%;
  height: 50px;
  background: linear-gradient(135deg, #a8b5c0 0%, #9ca3af 100%);
  border: none;
  border-radius: 12px;
  font-size: 18px;
  font-weight: 500;
  letter-spacing: 0.5px;
  margin-top: 10px;

  &:hover {
    background: linear-gradient(135deg, #9ca3af 0%, #8a939f 100%);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(156, 163, 175, 0.3);
  }

  &:disabled {
    opacity: 0.5;
  }
`

const SuccessCard = styled(Card)`
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(154, 163, 175, 0.15);
  background: #faf9f6;
  border: 1px solid rgba(168, 181, 192, 0.2);
  text-align: center;
  padding: 40px 20px;
`

const SuccessIcon = styled.div`
  font-size: 64px;
  color: #5a8a5a;
  margin-bottom: 20px;
`

const SuccessTitle = styled.h2`
  font-size: 24px;
  color: #5a5a5a;
  margin: 0 0 10px 0;
  font-weight: 500;
`

const SuccessMessage = styled.p`
  color: #8a8a8a;
  font-size: 16px;
  margin: 0 0 30px 0;
`

const BackButton = styled(Button)`
  border-radius: 8px;
  border-color: #d4c4b0;
  color: #5a5a5a;

  &:hover {
    border-color: #a8b5c0;
    color: #5a5a5a;
  }
`

const SubscriptionApplicationFormPage = () => {
  const [form] = Form.useForm()
  const [loading, setLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [submittedData, setSubmittedData] = useState(null)
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  // 从URL参数获取购买订单号
  const purchaseOrderNo = searchParams.get('purchaseOrderNo')

  React.useEffect(() => {
    if (purchaseOrderNo) {
      form.setFieldsValue({ purchaseOrderNo })
    }
  }, [purchaseOrderNo, form])

  const onFinish = async (values) => {
    setLoading(true)
    try {
      const submitData = {
        ...values,
        subscriptionDate: values.subscriptionDate
          ? values.subscriptionDate.format('YYYY-MM-DD')
          : undefined,
        expectedActivationDate: values.expectedActivationDate
          ? values.expectedActivationDate.format('YYYY-MM-DD')
          : undefined
      }

      const response = await api.post(
        '/api/subscription/applications',
        submitData
      )

      if (response.data.success) {
        setSuccess(true)
        setSubmittedData(response.data.data)
        message.success('申請表單提交成功！')
        form.resetFields()
      } else {
        message.error(response.data.message || '提交失敗，請重試')
      }
    } catch (error) {
      console.error('提交申請表單錯誤:', error)
      if (error.response?.status === 409) {
        message.error(
          error.response?.data?.message || '申請信息已存在，無法重複創建'
        )
      } else {
        message.error(
          error.response?.data?.message || '提交失敗，請檢查網絡連接或稍後再試'
        )
      }
    } finally {
      setLoading(false)
    }
  }

  const handleReset = () => {
    setSuccess(false)
    setSubmittedData(null)
    form.resetFields()
    if (purchaseOrderNo) {
      form.setFieldsValue({ purchaseOrderNo })
    }
  }

  if (success) {
    return (
      <PageContainer>
        <ContentWrapper>
          <SuccessCard>
            <SuccessIcon>
              <CheckCircleOutlined />
            </SuccessIcon>
            <SuccessTitle>申請提交成功！</SuccessTitle>
            <SuccessMessage>
              您的訂閱申請表單已成功提交，我們會盡快處理您的申請。
            </SuccessMessage>
            <Space>
              <BackButton icon={<ArrowLeftOutlined />} onClick={handleReset}>
                繼續填寫其他申請
              </BackButton>
            </Space>
          </SuccessCard>
        </ContentWrapper>
      </PageContainer>
    )
  }

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <Logo src={'/saa.png'} alt='公司logo' />
          <Title>訂閱申請表單</Title>
          <Subtitle>請填寫以下資訊以完成訂閱申請</Subtitle>
        </Header>

        <FormCard>
          <Form
            form={form}
            layout='vertical'
            onFinish={onFinish}
            autoComplete='off'
          >
            <FormItem
              name='qianjinSerialNumber'
              label='千金序號（可選）'
              tooltip='如有千金序號，請填寫'
            >
              <Input
                placeholder='請輸入千金專屬優惠代碼，沒有則無需填寫'
                size='large'
              />
            </FormItem>

            <FormItem
              name='purchaseOrderNo'
              label='購買訂單號'
              rules={[{ required: true, message: '請輸入購買訂單號' }]}
            >
              <Input
                placeholder='請輸入購買訂單號，例如：PERIOD1767609312592610'
                size='large'
                disabled={!!purchaseOrderNo}
              />
            </FormItem>

            <FormItem
              name='subscriptionDate'
              label='訂閱日期'
              rules={[{ required: true, message: '請選擇付款完成日期' }]}
            >
              <DatePicker
                style={{ width: '100%' }}
                size='large'
                format='YYYY-MM-DD'
                placeholder='請選擇付款完成日期'
                disabledDate={(current) =>
                  current && current > tzNow().endOf('day')
                }
              />
            </FormItem>

            <FormItem
              name='expectedActivationDate'
              label='預計開通日期'
              tooltip='請自行選擇預計開通日期，若為付款當日則無需選擇'
            >
              <DatePicker
                style={{ width: '100%' }}
                size='large'
                format='YYYY-MM-DD'
                placeholder='請選擇預計開通日期（可選）'
                disabledDate={(current) =>
                  current && current < tzNow().startOf('day')
                }
              />
            </FormItem>

            <FormItem
              name='account'
              label='帳號'
              rules={[
                { required: true, message: '請輸入帳號' },
                { type: 'email', message: '請輸入有效的電子郵件地址' }
              ]}
            >
              <Input
                type='email'
                placeholder='請輸入電子郵件地址'
                size='large'
              />
            </FormItem>

            <FormItem
              name='name'
              label='姓名'
              rules={[{ required: true, message: '請輸入姓名' }]}
            >
              <Input placeholder='請輸入姓名' size='large' />
            </FormItem>

            <FormItem
              name='phone'
              label='電話'
              rules={[
                { required: true, message: '請輸入電話' },
                {
                  pattern: /^[0-9]{8,10}$/,
                  message: '請輸入有效的電話號碼（8-10位數字）'
                }
              ]}
            >
              <Input placeholder='請輸入電話號碼' size='large' />
            </FormItem>

            <FormItem
              name='region'
              label='地區'
              rules={[{ required: true, message: '請輸入地區' }]}
            >
              <Input placeholder='請輸入地區，例如：台北市' size='large' />
            </FormItem>

            <FormItem
              name='storeName'
              label='店名'
              rules={[{ required: true, message: '請輸入店名' }]}
            >
              <Input placeholder='請輸入店名' size='large' />
            </FormItem>

            <FormItem
              name='storeType'
              label='店家類型'
              rules={[{ required: true, message: '請輸入店家類型' }]}
            >
              <Input
                placeholder='請輸入店家類型，例如：美髮店、美容院等'
                size='large'
              />
            </FormItem>

            <FormItem
              name='igOrWebsite'
              label='IG 或網站（可選）'
              rules={[{ type: 'url', message: '請輸入有效的網址' }]}
            >
              <Input
                placeholder='請輸入 Instagram 連結或網站網址'
                size='large'
              />
            </FormItem>

            <FormItem
              name='lineId'
              label='LineID（個人，聯繫使用）'
              rules={[{ required: true, message: '請輸入 LineID' }]}
            >
              <Input placeholder='請輸入 LineID' size='large' />
            </FormItem>

            <FormItem name='note' label='備註（可選）'>
              <TextArea
                rows={4}
                placeholder='如有其他需要說明的資訊，請在此填寫'
                style={{ resize: 'none' }}
              />
            </FormItem>

            <FormItem>
              <SubmitButton
                type='primary'
                htmlType='submit'
                loading={loading}
                disabled={loading}
              >
                {loading ? '提交中...' : '提交申請'}
              </SubmitButton>
            </FormItem>
          </Form>
        </FormCard>
      </ContentWrapper>
    </PageContainer>
  )
}

export default SubscriptionApplicationFormPage
